import { produce } from 'immer'
import lodash from 'lodash'
import { useCallback, useEffect, useMemo } from 'react'
import { useContextSelector } from 'use-context-selector'

import Field from 'components/controls/Field'
import type {
  IFieldConfig,
  IFieldRule,
  IOnChange,
  IOnChangeData,
} from 'components/controls/Field/Field.types'
import { FormContext } from 'components/controls/Form'

import { getTruthValue } from '../Field/getTruthValue'
import { validateRules } from '../Field/rules'
import { useFieldData, useFieldParams, useFormData } from './hooks'

const isRequired = <D,>(config: IFieldConfig<D>) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config.rules?.some((rule: IFieldRule<any>) => rule.required)

/** @deprecated use [FormKit](../../../components/formKit/index.ts) */
const FormField = <D,>({ config: c }: { config: IFieldConfig<D> }) => {
  const data = useFormData() as D
  const config = c.useConfig ? c.useConfig(data, c) : c
  const { name } = config
  const { value, initialValue, meta, defaultValue, fullValues } = useFieldData(name)
  const inheritValues = useContextSelector(FormContext, (context) => context.inheritValues) as D
  const layout = useContextSelector(FormContext, ({ layout }) => layout)
  const fieldSize = useContextSelector(FormContext, ({ size }) => size)
  const fieldConfig = useMemo(() => ({ layout, ...config }), [layout, config])

  const fieldParams = useFieldParams(config, data, inheritValues)
  const formDisabled = useContextSelector(FormContext, ({ disabled }) => disabled)
  const params = { ...fieldParams, disabled: formDisabled || fieldParams.disabled }
  const rules = config.rules

  const onChangeContext = useContextSelector(FormContext, (context) => context.onChange)
  const onChangeMeta = useContextSelector(FormContext, (context) => context.onChangeMeta)

  const onChange = useCallback(
    async (controlChangeData: IOnChangeData) => {
      const changeData = config.effect
        ? await config.effect(data, controlChangeData)
        : controlChangeData

      const { name, value } = changeData
      const validateError =
        !params.hidden && validateRules(getTruthValue(value, initialValue), rules)

      const newMeta = produce({ ...meta, ...changeData.meta }, (draft) => {
        draft.dirty = changeData.meta?.dirty || value !== initialValue
        draft.touched = true
        draft.valid = !validateError
        draft.error = validateError
        draft.hidden = params.hidden || false
        draft.disabled = params.disabled || false
      })

      onChangeContext({ name, value, meta: newMeta })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, initialValue, params.disabled, params.hidden, onChangeContext, rules],
  )

  const label =
    typeof config.label === 'function'
      ? config.label({
          data,
          config,
          onChange,
          value: fullValues,
          defaultValue,
          disabled: params.disabled,
        })
      : config.label

  useEffect(() => {
    const validateError = !params.hidden && validateRules(getTruthValue(value, initialValue), rules)

    const newMeta = produce(meta, (draft) => {
      draft.dirty = !lodash.isEqual(fullValues, initialValue)
      draft.valid = !validateError
      draft.error = validateError
      draft.hidden = params.hidden || false
      draft.disabled = params.disabled || false
      draft.commonError = false
    })
    onChangeMeta(name, newMeta)
    // potential bug with rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.rules, value, initialValue, params.disabled, onChangeMeta, params.hidden, rules])

  const handleOnChange: IOnChange = (changeData) => {
    onChange(changeData)
    config.dependsFields?.forEach(({ name, getValue }) => {
      const updateData = produce(data, (draft) => {
        lodash.set(draft as Record<string, unknown>, changeData.name, changeData.value)
      })
      const value = getValue(updateData)
      onChangeContext({ name, value })
    })
  }

  const handleOnBlur = () => onChangeMeta(name, { ...meta, touched: true })

  if (params.hidden) {
    return null
  }

  return (
    <Field
      config={fieldConfig}
      defaultValue={defaultValue}
      label={label}
      meta={meta}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      params={params}
      Parent={FormField}
      required={isRequired(config)}
      size={fieldSize}
      value={fullValues}
    />
  )
}

/** @deprecated use [FormKit](../../../components/formKit/index.ts) */
export default FormField
